import React, { useState, Component } from 'react';
import { MapPin, Clock, Info, Bike, ShoppingBag, UtensilsCrossed, Navigation, CalendarClock, X, Facebook, Instagram } from 'lucide-react';
import { Restaurant, RestaurantSettings } from '../../lib/types';
import { formatCurrency } from '../../lib/utils';
import { LanguageSelector } from '../ui/LanguageSelector';
import { useLanguage } from '../../lib/i18n';
import { isRestaurantOpenBySchedule } from '../../lib/schedule';
interface RestaurantHeaderProps {
  restaurant: Restaurant;
  settings: RestaurantSettings | null;
  zoneName?: string;
}
export function RestaurantHeader({
  restaurant,
  settings,
  zoneName
}: RestaurantHeaderProps) {
  const {
    t
  } = useLanguage();
  const [showSchedule, setShowSchedule] = useState(false);
  const scheduleStatus = isRestaurantOpenBySchedule(restaurant.slug);
  const isActuallyOpen = scheduleStatus.hasSchedule ? scheduleStatus.isOpen : false;
  const isEmilius = restaurant.slug === 'emilius-pizza';
  const isKabu = restaurant.slug === 'kabu';
  const isCongelados = restaurant.slug === 'heladeria-congelados';
  const isNatuCafe = restaurant.slug?.includes('natucafe') ?? false;
  const isCrackPollo = restaurant.slug === 'crack-pollo' || String(restaurant.id) === '20';
  const isCabanaParrillera = String(restaurant.id) === '24' || restaurant.slug === 'cabana-parrillera' || restaurant.slug === 'la-cabana-parrillera';
  const isRanchoSanFrancisco = String(restaurant.id) === '28' || restaurant.slug === 'rancho-san-francisco';
  const coverUrl = isEmilius ? 'https://res.cloudinary.com/djp4z0hdx/image/upload/v1778895392/ChatGPT_Image_15_may_2026_19_36_04_cdog6k.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : isCrackPollo ? 'https://cdn.magicpatterns.com/uploads/vkpirHwDmy1iyMJzGb5usA/ChatGPT_Image_30_abr_2026,_00_44_20.png' : restaurant.cover_url || 'https://cdn.magicpatterns.com/uploads/rYDkPPUtvynEvWDL4HujUk/portada.png';
  const logoUrl = isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : isCongelados ? 'https://cdn.magicpatterns.com/uploads/24WhrxVDroFFjBpfomMhk3/ChatGPT_Image_26_abr_2026,_23_23_00.png' : isCrackPollo ? 'https://cdn.magicpatterns.com/uploads/vrdnrQseiRNenTxSZL7mGp/ChatGPT_Image_30_abr_2026,_00_42_25.png' : restaurant.logo_url;
  return <div className={`${isNatuCafe ? 'bg-[#0F0D0B] border-[#2A2420]' : isCongelados ? 'bg-[#FBF5FF] border-[#E8DDF0]' : isCabanaParrillera ? 'bg-[#0B0807] border-[#3A1F0F]' : isRanchoSanFrancisco ? 'bg-[#0B0807] border-[#3A1F0F]' : 'bg-[#FBF7F2] border-[#E8DDD0]'} border-b`} data-id="element-2684">
      {/* Cover Image */}
      <div className={`relative h-44 sm:h-60 md:h-72 w-full ${isNatuCafe ? 'bg-[#1A1614]' : isCabanaParrillera ? 'bg-[#0B0807]' : isRanchoSanFrancisco ? 'bg-[#0B0807]' : 'bg-[#F5EDE3]'}`} data-id="element-2685">
        <img src={isCongelados ? 'https://cdn.magicpatterns.com/uploads/ar4FXudFfRgAEWhtKsiDih/ChatGPT_Image_22_abr_2026,_09_55_36.png' : coverUrl} alt={restaurant.name} className="w-full h-full object-cover" decoding="async" data-id="element-2686" />
        <div className={`absolute inset-0 bg-gradient-to-t ${isNatuCafe ? 'from-[#0F0D0B] via-black/50' : isCongelados ? 'from-[#FBF5FF] via-[#7B2D8E]/40' : isCabanaParrillera ? 'from-[#0B0807] via-[#1A0B05]/70' : isRanchoSanFrancisco ? 'from-[#0B0807] via-[#1A0B05]/70' : 'from-[#FBF7F2] via-black/40'} to-transparent`} data-id="element-2687" />

        {/* Badges */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 items-end" data-id="element-2688">
          <div className={`${isNatuCafe ? 'bg-[#1A1614]/90 border-[#2A2420]' : isCabanaParrillera ? 'bg-[#1A0B05]/90 border-[#3A1F0F]' : isRanchoSanFrancisco ? 'bg-[#1A0B05]/90 border-[#3A1F0F]' : 'bg-white/90 border-' + (isCongelados ? '[#E8DDF0]' : '[#E8DDD0]')} border backdrop-blur-md rounded-lg shadow-lg`} data-id="element-2689">
            <LanguageSelector data-id="element-2690" />
          </div>
          {restaurant.is_featured && <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${isNatuCafe ? 'bg-[#C6A969] text-[#0F0D0B]' : isCongelados ? 'bg-[#7B2D8E]' : isRanchoSanFrancisco ? 'bg-[#C6A969] text-[#0B0807]' : 'bg-[#8B5E3C]'} text-white shadow-lg`} data-id="element-2691">
              ★ {t('featured')}
            </span>}
          {!isActuallyOpen && <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${isNatuCafe ? 'bg-[#D4883A]' : isCongelados ? 'bg-[#F472B6]' : 'bg-[#CE1126]'} text-white shadow-lg`} data-id="element-2692">
              {t('closed')}
            </span>}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-2693">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-end -mt-10 sm:-mt-14 mb-5" data-id="element-2694">
          {/* Logo */}
          <div className={`relative w-20 h-20 sm:w-28 sm:h-28 rounded-2xl ${isNatuCafe ? 'bg-[#1A1614] border-[#2A2420]' : isCabanaParrillera ? 'bg-[#1A0B05] border-[#3A1F0F]' : isRanchoSanFrancisco ? 'bg-[#1A0B05] border-[#3A1F0F]' : 'bg-white border-' + (isCongelados ? '[#E8DDF0]' : '[#E8DDD0]')} p-1 shadow-md border shrink-0`} data-id="element-2695">
            {logoUrl ? <img src={logoUrl} alt={`${restaurant.name} logo`} className={`w-full h-full rounded-xl ${isNatuCafe ? 'object-contain bg-[#0F0D0B]' : isEmilius ? 'object-contain bg-[#F2E6D5]' : isKabu ? 'object-contain bg-[#E0B87B]' : isCongelados ? 'object-contain bg-white' : isCrackPollo ? 'object-contain bg-white' : 'object-cover'}`} decoding="async" data-id="element-2696" /> : <img src="https://cdn.magicpatterns.com/uploads/66K7i7HxpjApVZJyeR9rjp/ChatGPT_Image_15_mar_2026,_13_53_22.png" alt="NOIZA" className="w-full h-full rounded-xl object-cover" decoding="async" data-id="element-2697" />}
          </div>

          {/* Title & Info */}
          <div className="flex-1 pb-2" data-id="element-2698">
            <h1 className={`text-2xl sm:text-4xl font-black ${isNatuCafe ? 'text-[#F5F0EB]' : isCongelados ? 'text-[#7B2D8E]' : isCabanaParrillera ? 'text-[#FFB347]' : isRanchoSanFrancisco ? 'text-[#C6A969]' : 'text-[#3D2B1F]'} mb-1 tracking-tight`} style={{
            fontFamily: isNatuCafe ? "'Playfair Display', serif" : isCongelados ? "'Nunito', sans-serif" : "'Playfair Display', serif"
          }} data-id="element-2699">
              {restaurant.name} {isCongelados && '🍦'}{' '}
              {isCabanaParrillera && '🔥'}
            </h1>
            {restaurant.description_short && <p className={`${isNatuCafe ? 'text-[#C6A969]' : isCongelados ? 'text-[#5D3A1A]' : isCabanaParrillera ? 'text-[#E8C9A0]' : isRanchoSanFrancisco ? 'text-[#E8C9A0]' : 'text-[#8B7355]'} text-sm sm:text-base max-w-2xl mb-3`} data-id="element-2700">
                {restaurant.description_short}
              </p>}

            <div className={`flex flex-wrap items-center gap-3 text-sm ${isNatuCafe ? 'text-[#8B7B6B]' : isCongelados ? 'text-[#7B2D8E]/80' : isCabanaParrillera ? 'text-[#A89682]' : isRanchoSanFrancisco ? 'text-[#A89682]' : 'text-[#8B7355]/70'} font-medium`} data-id="element-2701">
              {(zoneName || restaurant.address_text) && <div className="flex items-center gap-1.5" data-id="element-2702">
                  <MapPin className="w-4 h-4" data-id="element-2703" />
                  {zoneName || restaurant.address_text}
                </div>}

              {settings?.average_prep_time_minutes && <div className="flex items-center gap-1.5" data-id="element-2704">
                  <Clock className="w-4 h-4" data-id="element-2705" />
                  {settings.average_prep_time_minutes} min
                </div>}

              {settings?.minimum_order_amount != null && settings.minimum_order_amount > 0 && <div className="flex items-center gap-1.5" data-id="element-2706">
                    <Info className="w-4 h-4" data-id="element-2707" />
                    Mín. {formatCurrency(settings.minimum_order_amount)}
                  </div>}
            </div>
          </div>
        </div>

        {/* Order Methods */}
        {settings && !isEmilius && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-2708">
            {settings.accept_delivery && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10 shadow-sm" style={{
          background: isNatuCafe ? 'linear-gradient(135deg, #C6A969 0%, #D4883A 100%)' : isCongelados ? 'linear-gradient(135deg, #7B2D8E 0%, #F472B6 100%)' : isCrackPollo ? 'linear-gradient(135deg, #F27B20 0%, #F5B731 100%)' : isCabanaParrillera ? 'linear-gradient(135deg, #FF6B35 0%, #F59E0B 100%)' : 'linear-gradient(135deg, #006847 0%, #2AABB3 100%)'
        }} data-id="element-2709">
                <Bike className="w-4 h-4" data-id="element-2710" /> {t('delivery')}
              </span>}
            {settings.accept_pickup && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10 shadow-sm" style={{
          background: isNatuCafe ? 'linear-gradient(135deg, #D4883A 0%, #8B5E3C 100%)' : isCongelados ? 'linear-gradient(135deg, #F472B6 0%, #D4A24E 100%)' : isCrackPollo ? 'linear-gradient(135deg, #F5B731 0%, #F27B20 100%)' : isCabanaParrillera ? 'linear-gradient(135deg, #F59E0B 0%, #DC2626 100%)' : 'linear-gradient(135deg, #CE1126 0%, #E8751A 100%)'
        }} data-id="element-2711">
                <ShoppingBag className="w-4 h-4" data-id="element-2712" /> {t('pickup')}
              </span>}
            {((settings as any).accept_dine_in || isCongelados || isNatuCafe || isCrackPollo || isCabanaParrillera || isRanchoSanFrancisco) && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10 shadow-sm" style={{
          background: isNatuCafe ? 'linear-gradient(135deg, #8B5E3C 0%, #C6A969 100%)' : isCongelados ? 'linear-gradient(135deg, #4A7FD4 0%, #7B2D8E 100%)' : isCrackPollo ? 'linear-gradient(135deg, #E86A10 0%, #F27B20 100%)' : isCabanaParrillera ? 'linear-gradient(135deg, #DC2626 0%, #7C2D12 100%)' : isRanchoSanFrancisco ? 'linear-gradient(135deg, #8B5E3C 0%, #5C3A1C 100%)' : 'linear-gradient(135deg, #6B2FA0 0%, #CE1126 100%)'
        }} data-id="element-2713">
                <UtensilsCrossed className="w-4 h-4" data-id="element-2714" />{' '}
                {isCabanaParrillera ? 'Comer en el local' : isRanchoSanFrancisco ? 'Para comer en el local' : t('dine_in')}
              </span>}
          </div>}

        {/* Action buttons — Congelados specific */}
        {isCongelados && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-2715">
            {(restaurant.lat && restaurant.lng || restaurant.address_text) && <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-white shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #7B2D8E 0%, #4A7FD4 100%)'
        }} data-id="element-2716">
                <Navigation className="w-4 h-4" data-id="element-2717" />
                ¿Cómo llegar?
              </a>}
            <button onClick={() => setShowSchedule(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#7B2D8E] border-2 border-[#7B2D8E]/30 bg-white shadow-sm transition-transform active:scale-95 hover:bg-[#7B2D8E]/5" data-id="element-2718">
              <CalendarClock className="w-4 h-4" data-id="element-2719" />
              Horario
            </button>
          </div>}

        {/* NatuCafé action buttons */}
        {isNatuCafe && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-2720">
            {(restaurant.lat && restaurant.lng || restaurant.address_text) && <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#0F0D0B] shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #C6A969 0%, #D4883A 100%)'
        }} data-id="element-2721">
                <Navigation className="w-4 h-4" data-id="element-2722" />
                ¿Cómo llegar?
              </a>}
            <button onClick={() => {
          alert('☕ Horario de NatuCafé Coffee Shop:\n\nLunes a Miércoles: 11:00 AM – 7:00 PM\nJueves a Domingo: 11:00 AM – 8:00 PM\n\n📍 Los Santos, Costa Rica');
        }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#C6A969] border-2 border-[#C6A969]/30 bg-transparent shadow-sm transition-transform active:scale-95 hover:bg-[#C6A969]/10" data-id="element-2723">
              <CalendarClock className="w-4 h-4" data-id="element-2724" />
              Horario
            </button>
          </div>}

        {/* Crack Pollo action buttons */}
        {isCrackPollo && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-2725">
            <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || 'Crack Pollo Los Santos Costa Rica')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-white shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #F27B20 0%, #F5B731 100%)'
        }} data-id="element-2726">
              <Navigation className="w-4 h-4" data-id="element-2727" />
              ¿Cómo llegar?
            </a>
            <button onClick={() => {
          alert("🍗 Horario de Crack'pollö:\n\nLunes a Sábado: 10:00 AM – 10:00 PM\nDomingo: Cerrado\n\n📍 Los Santos, Costa Rica");
        }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#F27B20] border-2 border-[#F27B20]/30 bg-white shadow-sm transition-transform active:scale-95 hover:bg-[#F27B20]/5" data-id="element-2728">
              <CalendarClock className="w-4 h-4" data-id="element-2729" />
              Horario
            </button>
          </div>}

        {/* La Cabaña Parrillera action buttons */}
        {isCabanaParrillera && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-2730">
            {(restaurant.lat && restaurant.lng || restaurant.address_text) && <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || 'La Cabaña Parrillera Costa Rica')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-white shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #FF6B35 0%, #F59E0B 100%)'
        }} data-id="element-2731">
                <Navigation className="w-4 h-4" data-id="element-2732" />
                ¿Cómo llegar?
              </a>}
            <button onClick={() => setShowSchedule(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#FFB347] border-2 border-[#FFB347]/40 bg-transparent shadow-sm transition-transform active:scale-95 hover:bg-[#FFB347]/10" data-id="element-2733">
              <CalendarClock className="w-4 h-4" data-id="element-2734" />
              Horario
            </button>
          </div>}

        {/* Emilius Pizza action buttons — replaces the generic
            Delivery/Llevar pills with: Cómo llegar, Horario, y redes
            sociales. El cliente igual puede pedir delivery / pickup
            desde el checkout — esto es sólo el bloque informativo
            arriba del menú. */}
        {isEmilius && <div className="flex flex-wrap items-center gap-2.5 pb-5" data-id="element-2735">
            {(restaurant.lat && restaurant.lng || restaurant.address_text) && <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || 'Emilius Pizza San Marcos de Tarrazu Costa Rica')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-white shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #6FA248 0%, #4F7C2E 100%)'
        }} data-id="element-2736">
                <Navigation className="w-4 h-4" data-id="element-2737" />
                ¿Cómo llegar?
              </a>}
            <button onClick={() => setShowSchedule(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#6FA248] border-2 border-[#6FA248]/40 bg-transparent shadow-sm transition-transform active:scale-95 hover:bg-[#6FA248]/10" data-id="element-2738">
              <CalendarClock className="w-4 h-4" data-id="element-2739" />
              Horario
            </button>
            {/* Social: Facebook */}
            <a href="https://www.facebook.com/EmiliusPizza" target="_blank" rel="noopener noreferrer" aria-label="Facebook de Emilius Pizza" className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white shadow-md transition-transform active:scale-95 hover:opacity-90" style={{
          background: '#1877F2'
        }} data-id="element-2740">
              <Facebook className="w-5 h-5" data-id="element-2741" />
            </a>
            {/* Social: Instagram */}
            <a href="https://www.instagram.com/emilius_pizza/" target="_blank" rel="noopener noreferrer" aria-label="Instagram de Emilius Pizza" className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white shadow-md transition-transform active:scale-95 hover:opacity-90" style={{
          background: 'linear-gradient(135deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%)'
        }} data-id="element-2742">
              <Instagram className="w-5 h-5" data-id="element-2743" />
            </a>
            {/* Llamar — abre la app de llamadas en el celular */}
            <a href="tel:+50625461111" aria-label="Llamar a Emilius Pizza" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#6FA248] border-2 border-[#6FA248]/40 bg-transparent shadow-sm transition-transform active:scale-95 hover:bg-[#6FA248]/10" data-id="element-2744">
              <span className="text-base leading-none" data-id="element-2745">📞</span>
              Llamar
            </a>
          </div>}

        {/* Rancho San Francisco action buttons */}
        {isRanchoSanFrancisco && <div className="flex flex-wrap items-center gap-2.5 pb-5" data-id="element-2746">
            {(restaurant.lat && restaurant.lng || restaurant.address_text) && <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || 'Rancho San Francisco Costa Rica')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-white shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #8B5E3C 0%, #5C3A1C 100%)'
        }} data-id="element-2747">
                <Navigation className="w-4 h-4" data-id="element-2748" />
                ¿Cómo llegar?
              </a>}
            <button onClick={() => setShowSchedule(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#C6A969] border-2 border-[#C6A969]/40 bg-transparent shadow-sm transition-transform active:scale-95 hover:bg-[#C6A969]/10" data-id="element-2749">
              <CalendarClock className="w-4 h-4" data-id="element-2750" />
              Horario
            </button>
            {/* Social: Facebook */}
            <a href="https://www.facebook.com/profile.php?id=100055957147454" target="_blank" rel="noopener noreferrer" aria-label="Facebook de Rancho San Francisco" className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white shadow-md transition-transform active:scale-95 hover:opacity-90" style={{
          background: '#1877F2'
        }} data-id="element-2751">
              <Facebook className="w-5 h-5" data-id="element-2752" />
            </a>
            {/* Social: Instagram */}
            <a href="https://www.instagram.com/rancho_san_fracisco?fbclid=IwY2xjawR2t1tleHRuA2FlbQIxMABicmlkETFtZEx6S1BMS2ZkQVdZZVNQc3J0YwZhcHBfaWQQMjIyMDM5MTc4ODIwMDg5MgABHg9eElxVfCBoB3Z-6VezBcRzn3P5XL5_ER_XB0AXrphZbOTJCdtYqe-16E0i_aem_UIu3T-i-uzc9puaYLWrdzQ" target="_blank" rel="noopener noreferrer" aria-label="Instagram de Rancho San Francisco" className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white shadow-md transition-transform active:scale-95 hover:opacity-90" style={{
          background: 'linear-gradient(135deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%)'
        }} data-id="element-2753">
              <Instagram className="w-5 h-5" data-id="element-2754" />
            </a>
          </div>}
      </div>

      {/* Congelados Schedule Modal */}
      {showSchedule && isCongelados && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowSchedule(false)} data-id="element-2755">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-2756" />
          <div className="relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #FBF5FF 0%, #F3E8FA 100%)'
      }} data-id="element-2757">
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4 text-center" style={{
          background: 'linear-gradient(135deg, #7B2D8E 0%, #9B4DCA 50%, #F472B6 100%)'
        }} data-id="element-2758">
              <button onClick={() => setShowSchedule(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors" data-id="element-2759">
                <X className="w-4 h-4" data-id="element-2760" />
              </button>
              <span className="text-4xl mb-2 block" data-id="element-2761">🍦</span>
              <h3 className="text-xl font-black text-white tracking-tight" style={{
            fontFamily: "'Nunito', sans-serif"
          }} data-id="element-2762">
                Nuestro Horario
              </h3>
              <p className="text-white/80 text-xs mt-1" data-id="element-2763">Heladería Congelados</p>
            </div>

            {/* Schedule */}
            <div className="px-6 py-5 space-y-2" data-id="element-2764">
              {[{
            day: 'Lunes',
            hours: '10:00 AM – 6:00 PM'
          }, {
            day: 'Martes',
            hours: '10:00 AM – 6:00 PM'
          }, {
            day: 'Miércoles',
            hours: '10:00 AM – 6:00 PM'
          }, {
            day: 'Jueves',
            hours: '10:00 AM – 5:00 PM'
          }, {
            day: 'Viernes',
            hours: '10:00 AM – 6:00 PM'
          }, {
            day: 'Sábado',
            hours: '10:00 AM – 6:00 PM'
          }, {
            day: 'Domingo',
            hours: '1:00 PM – 6:00 PM'
          }].map(({
            day,
            hours
          }) => {
            const today = new Date().toLocaleDateString('es-CR', {
              weekday: 'long'
            });
            const isToday = today.toLowerCase() === day.toLowerCase();
            return <div key={day} className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-colors ${isToday ? 'bg-[#7B2D8E]/10 border border-[#7B2D8E]/20' : 'bg-white/60'}`} data-id="element-2765">
                    <span className={`font-bold ${isToday ? 'text-[#7B2D8E]' : 'text-[#5D3A1A]'}`} data-id="element-2766">
                      {isToday ? `📍 ${day}` : day}
                    </span>
                    <span className={`font-semibold ${isToday ? 'text-[#7B2D8E]' : 'text-[#5D3A1A]/70'}`} data-id="element-2767">
                      {hours}
                    </span>
                  </div>;
          })}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6" data-id="element-2768">
              <button onClick={() => setShowSchedule(false)} className="w-full py-3 rounded-2xl text-sm font-bold text-white shadow-lg transition-transform active:scale-95" style={{
            background: 'linear-gradient(135deg, #7B2D8E 0%, #F472B6 100%)'
          }} data-id="element-2769">
                ¡Te esperamos! 🍨
              </button>
            </div>
          </div>
        </div>}

      {/* La Cabaña Parrillera Schedule Modal */}
      {showSchedule && isCabanaParrillera && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowSchedule(false)} data-id="element-2770">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-2771" />
          <div className="relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden border border-[#3A1F0F]" onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #1A0B05 0%, #0B0807 100%)'
      }} data-id="element-2772">
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4 text-center" style={{
          background: 'linear-gradient(135deg, #7C2D12 0%, #DC2626 40%, #FF6B35 75%, #F59E0B 100%)'
        }} data-id="element-2773">
              <button onClick={() => setShowSchedule(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50 transition-colors" data-id="element-2774">
                <X className="w-4 h-4" data-id="element-2775" />
              </button>
              <span className="text-4xl mb-2 block" data-id="element-2776">🔥</span>
              <h3 className="text-xl font-black text-white tracking-tight" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-2777">
                Nuestro Horario
              </h3>
              <p className="text-white/85 text-xs mt-1" data-id="element-2778">La Cabaña Parrillera</p>
            </div>

            {/* Schedule */}
            <div className="px-6 py-5 space-y-2" data-id="element-2779">
              {[{
            day: 'Lunes',
            hours: 'Cerrado',
            open: false
          }, {
            day: 'Martes',
            hours: 'Cerrado',
            open: false
          }, {
            day: 'Miércoles',
            hours: 'Cerrado',
            open: false
          }, {
            day: 'Jueves',
            hours: '5:00 PM – 9:00 PM',
            open: true
          }, {
            day: 'Viernes',
            hours: '5:00 PM – 9:00 PM',
            open: true
          }, {
            day: 'Sábado',
            hours: '12:00 MD – 9:00 PM',
            open: true
          }, {
            day: 'Domingo',
            hours: '12:00 MD – 7:00 PM',
            open: true
          }].map(({
            day,
            hours,
            open
          }) => {
            const today = new Date().toLocaleDateString('es-CR', {
              weekday: 'long'
            });
            const isToday = today.toLowerCase() === day.toLowerCase();
            return <div key={day} className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-colors border ${isToday ? 'bg-[#FF6B35]/15 border-[#FF6B35]/40' : 'bg-white/5 border-white/5'}`} data-id="element-2780">
                    <span className={`font-bold ${isToday ? 'text-[#FFB347]' : open ? 'text-[#E8C9A0]' : 'text-[#A89682]/60'}`} data-id="element-2781">
                      {isToday ? `🔥 ${day}` : day}
                    </span>
                    <span className={`font-semibold ${isToday ? 'text-[#FFB347]' : open ? 'text-[#E8C9A0]/85' : 'text-[#A89682]/50'}`} data-id="element-2782">
                      {hours}
                    </span>
                  </div>;
          })}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6" data-id="element-2783">
              <button onClick={() => setShowSchedule(false)} className="w-full py-3 rounded-2xl text-sm font-bold text-white shadow-lg transition-transform active:scale-95" style={{
            background: 'linear-gradient(135deg, #DC2626 0%, #FF6B35 50%, #F59E0B 100%)'
          }} data-id="element-2784">
                ¡Te esperamos! 🔥
              </button>
            </div>
          </div>
        </div>}

      {/* Emilius Pizza Schedule Modal — mismo patrón que Rancho/Cabaña
              pero con el tema oscuro y el verde de Emilius (#6FA248).
              Los horarios son los actuales del restaurante; si el dueño
              los cambia, ajustamos este bloque (no hay business_hours en
              el schema todavía para Emilius). */}
      {showSchedule && isEmilius && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowSchedule(false)} data-id="element-2785">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-2786" />
          <div className="relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden border border-white/10" onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #111111 0%, #0B0B0B 100%)'
      }} data-id="element-2787">
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4 text-center" style={{
          background: 'linear-gradient(135deg, #3F6126 0%, #5C8A35 50%, #6FA248 100%)'
        }} data-id="element-2788">
              <button onClick={() => setShowSchedule(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50 transition-colors" data-id="element-2789">
                <X className="w-4 h-4" data-id="element-2790" />
              </button>
              <span className="text-4xl mb-2 block" data-id="element-2791">🍕</span>
              <h3 className="text-xl font-black text-white tracking-tight" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-2792">
                Nuestro Horario
              </h3>
              <p className="text-white/85 text-xs mt-1" data-id="element-2793">Emilius Pizza</p>
            </div>

            {/* Schedule — horarios placeholder; el dueño los confirma y
                actualizamos. Por ahora: Lun cerrado, Mar-Dom 11:00 AM – 10:00 PM */}
            <div className="px-6 py-5 space-y-2" data-id="element-2794">
              {[{
            day: 'Lunes',
            hours: 'Cerrado',
            open: false
          }, {
            day: 'Martes',
            hours: '11:00 AM – 10:00 PM',
            open: true
          }, {
            day: 'Miércoles',
            hours: '11:00 AM – 10:00 PM',
            open: true
          }, {
            day: 'Jueves',
            hours: '11:00 AM – 10:00 PM',
            open: true
          }, {
            day: 'Viernes',
            hours: '11:00 AM – 10:00 PM',
            open: true
          }, {
            day: 'Sábado',
            hours: '11:00 AM – 10:00 PM',
            open: true
          }, {
            day: 'Domingo',
            hours: '11:00 AM – 10:00 PM',
            open: true
          }].map(({
            day,
            hours,
            open
          }) => {
            const today = new Date().toLocaleDateString('es-CR', {
              weekday: 'long'
            });
            const isToday = today.toLowerCase() === day.toLowerCase();
            return <div key={day} className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-colors border ${isToday ? 'bg-[#6FA248]/15 border-[#6FA248]/40' : 'bg-white/5 border-white/5'}`} data-id="element-2795">
                    <span className={`font-bold ${isToday ? 'text-[#6FA248]' : open ? 'text-white/85' : 'text-white/40'}`} data-id="element-2796">
                      {isToday ? `🍕 ${day}` : day}
                    </span>
                    <span className={`font-semibold ${isToday ? 'text-[#6FA248]' : open ? 'text-white/70' : 'text-[#B45A4A]'}`} data-id="element-2797">
                      {hours}
                    </span>
                  </div>;
          })}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6" data-id="element-2798">
              <button onClick={() => setShowSchedule(false)} className="w-full py-3 rounded-2xl text-sm font-bold text-white shadow-lg transition-transform active:scale-95" style={{
            background: 'linear-gradient(135deg, #3F6126 0%, #5C8A35 50%, #6FA248 100%)'
          }} data-id="element-2799">
                ¡Te esperamos! 🍕
              </button>
            </div>
          </div>
        </div>}

      {/* Rancho San Francisco Schedule Modal */}
      {showSchedule && isRanchoSanFrancisco && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowSchedule(false)} data-id="element-2800">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" data-id="element-2801" />
          <div className="relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden border border-[#8B5E3C]/30" onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #FBF7F2 0%, #F2E6D5 100%)'
      }} data-id="element-2802">
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4 text-center" style={{
          background: 'linear-gradient(135deg, #5C3A1C 0%, #8B5E3C 60%, #C6A969 100%)'
        }} data-id="element-2803">
              <button onClick={() => setShowSchedule(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/20 flex items-center justify-center text-white hover:bg-black/40 transition-colors" data-id="element-2804">
                <X className="w-4 h-4" data-id="element-2805" />
              </button>
              <span className="text-4xl mb-2 block" data-id="element-2806">🐷</span>
              <h3 className="text-xl font-black text-white tracking-tight" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-2807">
                Nuestro Horario
              </h3>
              <p className="text-white/85 text-xs mt-1" data-id="element-2808">Rancho San Francisco</p>
            </div>

            {/* Schedule — Lun a Mié cerrado, Jue a Dom 10:00 AM – 10:00 PM */}
            <div className="px-6 py-5 space-y-2" data-id="element-2809">
              {[{
            day: 'Lunes',
            hours: 'Cerrado',
            open: false
          }, {
            day: 'Martes',
            hours: 'Cerrado',
            open: false
          }, {
            day: 'Miércoles',
            hours: 'Cerrado',
            open: false
          }, {
            day: 'Jueves',
            hours: '10:00 AM – 10:00 PM',
            open: true
          }, {
            day: 'Viernes',
            hours: '10:00 AM – 10:00 PM',
            open: true
          }, {
            day: 'Sábado',
            hours: '10:00 AM – 10:00 PM',
            open: true
          }, {
            day: 'Domingo',
            hours: '10:00 AM – 10:00 PM',
            open: true
          }].map(({
            day,
            hours,
            open
          }) => {
            const today = new Date().toLocaleDateString('es-CR', {
              weekday: 'long'
            });
            const isToday = today.toLowerCase() === day.toLowerCase();
            return <div key={day} className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-sm border ${isToday ? 'bg-[#8B5E3C]/10 border-[#8B5E3C]/30' : 'bg-white/70 border-[#E8DDD0]'}`} data-id="element-2810">
                    <span className={`font-bold ${isToday ? 'text-[#5C3A1C]' : 'text-[#5C3A1C]/80'}`} data-id="element-2811">
                      {isToday ? `🐷 ${day}` : day}
                    </span>
                    <span className={`font-semibold ${open ? isToday ? 'text-[#5C3A1C]' : 'text-[#8B7355]' : 'text-[#B45A4A]'}`} data-id="element-2812">
                      {hours}
                    </span>
                  </div>;
          })}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6" data-id="element-2813">
              <button onClick={() => setShowSchedule(false)} className="w-full py-3 rounded-2xl text-sm font-bold text-white shadow-lg transition-transform active:scale-95" style={{
            background: 'linear-gradient(135deg, #5C3A1C 0%, #8B5E3C 50%, #C6A969 100%)'
          }} data-id="element-2814">
                ¡Te esperamos! 🐷
              </button>
            </div>
          </div>
        </div>}
    </div>;
}